<template>
	<div v-if="targets.length > 1" class="c-search-list-layout-toc">
		<span
			ref="scrollContainer"
			class="
				flex flex-wrap
				items-center
				justify-center
				w-full
				min-w-0
				max-w-full
				h-full
			"
		>
			<fieldset
				:class="[
					'flex flex-wrap items-center',
					'gap-x-14 gap-y-8 >=1024:gap-x-xs/h',
					'w-fit h-full flex-shrink-0',

					{
						'justify-start': align == 'left',
						'justify-center': align == 'center',
						'justify-end': align == 'right',
					},
				]"
			>
				<label
					v-for="{ id, tocItemId, title, metaData } in targets"
					:key="id"
					class="
						group
						relative
						w-max
						max-w-full
						flex-0
						inline-flex
						justify-center
						items-center
						gap-x-8
						font-medium
						text-body-xs
					"
					:class="{
						'text-theme': activeId === id,
					}"
				>
					<input
						:id="tocItemId"
						type="radio"
						name="c-search-list-layout-toc__radio"
						class="
							appearance-none
							absolute
							left-0
							top-0
							w-full
							h-full
							-z-1
						"
						:value="id"
						:checked="activeId === id"
						:tabinsdex="activeId === id ? '0' : '-1'"
						@change="() => scrollToTarget(id)"
						@click="(e) => selectTarget(id, e)"
						@keydown.enter="(e) => selectTarget(id, e)"
					/>
					<span
						:class="{
							'text-gray-darker': activeId !== id,
						}"
						v-text="title"
					></span>
					<span
						class="
							inline-block
							px-8
							py-4
							rounded-full
							text-label-sm
							align-baseline
						"
						:class="{
							'bg-gray-light': activeId !== id,
							'bg-theme-40 bg-opacity-30': activeId === id,
						}"
						v-text="metaData.total"
					></span>
				</label>
			</fieldset>

			<slot></slot>
		</span>
	</div>
</template>

<script>
export default {
	name: 'SearchListLayoutToc',
	props: {
		data: {
			type: Object,
			default: () => ({}),
		},

		actions: {
			type: Object,
			default: () => ({}),
		},

		align: {
			type: String,
			default: 'center',
			validator: (v) => ['left', 'center', 'right'].includes(v),
		},
	},

	data() {
		return {
			isScrollable: false,
			scrollShadeAlphaLeft: 0,
			scrollShadeAlphaRight: 0,
			activeId: null,
			observer: null,
			frameRequest: null,
		};
	},

	computed: {
		activeTarget() {
			return this.data?.activeTarget || {};
		},

		targets() {
			return (
				this.data?.targets?.filter(
					({ metaData }) => !metaData.hidden
				) || []
			);
		},
	},

	watch: {
		activeTarget: {
			immediate: true,
			handler(activeTarget, previousTarget) {
				if (
					typeof window !== 'undefined' &&
					activeTarget?.id !== previousTarget?.id
				) {
					window.cancelAnimationFrame(this.frameRequest);
					this.activeId = this.activeId || activeTarget.id;
					this.frameRequest = window.requestAnimationFrame(() => {
						const el =
							activeTarget.tocItemId &&
							document.getElementById(activeTarget.tocItemId)
								?.parentElement;
						if (el) {
							el.parentElement.parentElement.scrollBy({
								left:
									el.offsetLeft -
									el.parentElement.parentElement.scrollLeft -
									48,
								behavior: 'smooth',
							});
						}

						window.setTimeout(() => {
							if (
								!this.isScrollable &&
								this.$el &&
								document.activeElement &&
								this.$el.contains(document.activeElement)
							) {
								return;
							}
							this.activeId = activeTarget.id;
						}, 0);
					});
				}
			},
		},
	},

	methods: {
		setFocus(el) {
			const { tabIndex } = el;
			const hasTabIndex = el.hasAttribute('tabindex');
			el.setAttribute('tabindex', '-1');

			try {
				el.focus({
					preventScroll: true,
				});
			} catch (e) {
				el.focus();
			}

			if (hasTabIndex) {
				el.setAttribute('tabindex', tabIndex);
			} else {
				el.removeAttribute('tabindex');
			}
		},

		scrollToTarget(id) {
			this.activeId = id;
			const targetEl = id && document.getElementById(id);
			if (targetEl) {
				let scrollMarginTop =
					Number(
						getComputedStyle(document.body)[
							'scroll-margin-top'
						]?.replace('px', '')
					) * 1.5;
				if (targetEl.offsetTop > window.scrollY) {
					scrollMarginTop /= 2;
				}

				try {
					window.scrollTo({
						top: targetEl.offsetTop - scrollMarginTop,
						behavior: this.checkPreferReducedMotion()
							? 'auto'
							: 'smooth',
					});
				} catch (e) {
					window.scrollTo(0, targetEl.offsetTop - scrollMarginTop);
				}
			}
		},

		selectTarget(id, e) {
			const targetEl =
				(e.pointerType || e.key === 'Enter') &&
				id &&
				document.getElementById(id);
			if (targetEl) {
				// this.scrollToTarget(id);
				this.setFocus(targetEl);
			}
		},

		checkPreferReducedMotion() {
			return window.matchMedia('(prefers-reduced-motion: reduce)')
				.matches;
		},
	},
};
</script>

<style lang="postcss">
.c-search-list-layout-toc {
	@apply relative flex items-center w-fit min-w-0 whitespace-nowrap;
	&:after {
		content: '';
		position: absolute;
		top: 0;
		left: -1px;
		width: calc(100% + 2px);
		height: 100%;
		background: linear-gradient(
				to left,
				rgba(255, 255, 255, 0),
				rgba(255, 255, 255, var(--scroll-shade-alpha-left, 0)) 100%
			),
			linear-gradient(
					to right,
					rgba(255, 255, 255, 0),
					rgba(255, 255, 255, var(--scroll-shade-alpha-right, 0)) 100%
				)
				100% 0;
		background-repeat: no-repeat;
		background-attachment: local, local;
		background-size: 60px 100%, 60px 100%;
		transition: background 0.3s;
		pointer-events: none;
	}
}
</style>
